import {
  BorderBottom24Regular,
  BorderLeft24Regular,
  BorderRight24Regular,
  BorderTop24Regular,
} from '@fluentui/react-icons';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { buildGeometryScene } from '@av-platform/rendering-core';
import { Checkbox } from '@/components/ui/Checkbox';

interface Layer {
  name: string;
  visible: boolean;
}

interface ViewProps {
  geometry: any;
  canvasId: string;
  editable?: boolean;
  layers: Layer[];
  position?: string;
  withControlPanel?: boolean;
  onCameraChange?: (camera: THREE.Camera) => void;
  onChangeLayerVisibility?: (layer: { name: string; visible: boolean }) => void;
  cameraType?: 'orthogonal' | 'perspective';
  zoom?: number;
  submit?: () => void;
  controlPanel?: React.ReactNode;
}

const View = ({
  geometry,
  canvasId,
  editable,
  layers,
  position,
  withControlPanel,
  onCameraChange,
  onChangeLayerVisibility,
  cameraType,
  zoom,
}: ViewProps) => {
  const [boundingBoxSize, setBoundingBoxSize] = useState<THREE.Vector3>();

  const canvasRef = useRef<HTMLCanvasElement>();
  const scene = useRef(new THREE.Scene());
  const camera =
    cameraType === 'orthogonal'
      ? useRef<THREE.OrthographicCamera | THREE.PerspectiveCamera>(
          new THREE.OrthographicCamera(-1340, 1340, 1340, -1340, 0, 1000000000)
        )
      : useRef<THREE.OrthographicCamera | THREE.PerspectiveCamera>(
          new THREE.PerspectiveCamera(45, 1, 1, 1000000000)
        );

  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const controls = useRef<OrbitControls>();
  const isPerspective = cameraType === 'perspective';

  const changeCamera = () => {
    onCameraChange && onCameraChange(camera.current);
  };

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }

    const cs = canvasRef.current;

    const result = buildGeometryScene({
      geometry,
      layers,
      perspective: cameraType !== 'orthogonal',
      zoom: zoom ?? 1,
      position,
    });

    scene.current = result.scene;
    camera.current = result.camera as THREE.OrthographicCamera | THREE.PerspectiveCamera;
    setBoundingBoxSize(result.boundingBoxSize);

    rendererRef.current =
      rendererRef.current || new THREE.WebGLRenderer({ canvas: cs, antialias: true });

    changeCamera();

    controls.current = new OrbitControls(camera.current, rendererRef.current.domElement);
    controls.current.enableDamping = true;
    controls.current.dampingFactor = 0.04;
    controls.current.rotateSpeed = 1;
    controls.current.addEventListener('change', changeCamera);

    if (editable) {
      animate();
    } else {
      rendererRef.current.render(scene.current, camera.current);
    }
  }, [geometry, position, cameraType, canvasId, editable, zoom, layers]);

  const animate = () => {
    requestAnimationFrame(animate);
    rendererRef.current!.render(scene.current, camera.current);
  };

  const setCameraPosition = () => {
    if (!isPerspective && boundingBoxSize) {
      const cameraWidth = boundingBoxSize.x * 0.55;
      const cameraHeight = boundingBoxSize.y * 0.55;
      const cameraPosition = cameraHeight > cameraWidth ? cameraHeight : cameraWidth;

      (camera.current as THREE.OrthographicCamera).left = cameraPosition / -1;
      (camera.current as THREE.OrthographicCamera).right = cameraPosition / 1;
      (camera.current as THREE.OrthographicCamera).top = cameraPosition / 1;
      (camera.current as THREE.OrthographicCamera).bottom = cameraPosition / -1;
      camera.current.zoom = 1;

      camera.current.updateProjectionMatrix();
    }
  };

  const changeViewRight = () => {
    if (isPerspective && boundingBoxSize) {
      const fov = (camera.current as THREE.PerspectiveCamera).fov * (Math.PI / 180);
      const distance = Math.max(
        boundingBoxSize.z / (2 * Math.tan(fov / 2)),
        boundingBoxSize.y / (2 * Math.tan(fov / 2))
      );

      camera.current.position.set(-distance - 600, 0, 0);
    } else {
      camera.current.position.set(-100, 0, 0);
    }

    camera.current.lookAt(0, 0, 0);

    setCameraPosition();
    controls.current!.update();
    onCameraChange!(camera.current);
  };

  const changeViewLeft = () => {
    if (isPerspective && boundingBoxSize) {
      const fov = (camera.current as THREE.PerspectiveCamera).fov * (Math.PI / 180);
      const distance = Math.max(
        boundingBoxSize.z / (2 * Math.tan(fov / 2)),
        boundingBoxSize.y / (2 * Math.tan(fov / 2))
      );

      camera.current.position.set(distance + 600, 0, 0);
    } else {
      camera.current.position.set(100, 0, 0);
    }

    camera.current.lookAt(0, 0, 0);
    setCameraPosition();
    controls.current!.update();
    onCameraChange!(camera.current);
  };

  const changeViewFront = () => {
    if (isPerspective && boundingBoxSize) {
      const fov = (camera.current as THREE.PerspectiveCamera).fov * (Math.PI / 180);
      const distance = Math.max(
        boundingBoxSize.x / (2 * Math.tan(fov / 2)),
        boundingBoxSize.y / (2 * Math.tan(fov / 2))
      );
      camera.current.position.set(0, 0, boundingBoxSize.z / 2 + distance + 100);
    } else {
      const point = new THREE.Vector3(0, 0, 100);

      camera.current.position.copy(point);
    }

    camera.current.updateProjectionMatrix();
    setCameraPosition();
    controls.current!.update();
    onCameraChange!(camera.current);
  };

  const changeViewBack = () => {
    if (isPerspective && boundingBoxSize) {
      const fov = (camera.current as THREE.PerspectiveCamera).fov * (Math.PI / 180);
      const distance = Math.max(
        boundingBoxSize.x / (2 * Math.tan(fov / 2)),
        boundingBoxSize.y / (2 * Math.tan(fov / 2))
      );
      camera.current.position.set(0, 0, -boundingBoxSize.z / 2 - distance - 100);
    } else {
      const point = new THREE.Vector3(0, 0, -100);
      camera.current.position.copy(point);
    }
    // camera.current.lookAt(0, 0, 0);

    // camera.current.zoom = 0.15;
    setCameraPosition();
    controls.current!.update();
    onCameraChange!(camera.current);

    controls.current!.update();
    onCameraChange!(camera.current);
  };

  const changeViewTopRightFrontCorner = () => {
    const fov = (camera.current as THREE.PerspectiveCamera).fov * (Math.PI / 180);
    const distance = Math.max(
      boundingBoxSize!.x / (2 * Math.tan(fov / 2)),
      boundingBoxSize!.y / (2 * Math.tan(fov / 2))
    );

    const positionPoint = new THREE.Vector3(
      boundingBoxSize!.x / 2,
      boundingBoxSize!.y / 2,
      boundingBoxSize!.z / 2
    );

    const targetPoint = new THREE.Vector3(
      boundingBoxSize!.x / -2,
      boundingBoxSize!.y / -2,
      boundingBoxSize!.z / -2
    );

    if (isPerspective) {
      const direction = new THREE.Vector3();
      direction.subVectors(positionPoint, targetPoint).normalize();

      positionPoint.addScaledVector(direction, distance);
    }

    camera.current.position.copy(positionPoint);
    camera.current.lookAt(targetPoint);
    // camera.current.zoom = 0.15;
    setCameraPosition();
    controls.current!.update();
    camera.current.updateProjectionMatrix();
  };

  const changeViewTopLeftFrontCorner = () => {
    const fov = (camera.current as THREE.PerspectiveCamera).fov * (Math.PI / 180);
    const distance = Math.max(
      boundingBoxSize!.x / (2 * Math.tan(fov / 2)),
      boundingBoxSize!.y / (2 * Math.tan(fov / 2))
    );

    const positionPoint = new THREE.Vector3(
      boundingBoxSize!.x / -2,
      boundingBoxSize!.y / 2,
      boundingBoxSize!.z / 2
    );

    const targetPoint = new THREE.Vector3(
      boundingBoxSize!.x / 2,
      boundingBoxSize!.y / -2,
      boundingBoxSize!.z / -2
    );

    if (isPerspective) {
      const direction = new THREE.Vector3();
      direction.subVectors(positionPoint, targetPoint).normalize();

      positionPoint.addScaledVector(direction, distance);
    }

    camera.current.position.copy(positionPoint);
    camera.current.lookAt(targetPoint);
    setCameraPosition();
    controls.current!.update();
    camera.current.updateProjectionMatrix();
  };

  const changeViewTopLeftBackCorner = () => {
    const fov = (camera.current as THREE.PerspectiveCamera).fov * (Math.PI / 180);
    const distance = Math.max(
      boundingBoxSize!.x / (2 * Math.tan(fov / 2)),
      boundingBoxSize!.y / (2 * Math.tan(fov / 2))
    );

    const positionPoint = new THREE.Vector3(
      boundingBoxSize!.x / -2,
      boundingBoxSize!.y / 2,
      boundingBoxSize!.z / -2
    );

    const targetPoint = new THREE.Vector3(
      boundingBoxSize!.x / 2,
      boundingBoxSize!.y / -2,
      boundingBoxSize!.z / 2
    );

    if (isPerspective) {
      const direction = new THREE.Vector3();
      direction.subVectors(positionPoint, targetPoint).normalize();

      positionPoint.addScaledVector(direction, distance);
    }

    camera.current.position.copy(positionPoint);
    camera.current.lookAt(targetPoint);
    setCameraPosition();
    controls.current!.update();
    camera.current.updateProjectionMatrix();
  };

  const changeViewTopRightBackCorner = () => {
    const fov = (camera.current as THREE.PerspectiveCamera).fov * (Math.PI / 180);
    const distance = Math.max(
      boundingBoxSize!.x / (2 * Math.tan(fov / 2)),
      boundingBoxSize!.y / (2 * Math.tan(fov / 2))
    );

    const positionPoint = new THREE.Vector3(
      boundingBoxSize!.x / 2,
      boundingBoxSize!.y / 2,
      boundingBoxSize!.z / -2
    );

    const targetPoint = new THREE.Vector3(
      boundingBoxSize!.x / -2,
      boundingBoxSize!.y / -2,
      boundingBoxSize!.z / 2
    );

    if (isPerspective) {
      const direction = new THREE.Vector3();
      direction.subVectors(positionPoint, targetPoint).normalize();

      positionPoint.addScaledVector(direction, distance);
    }

    camera.current.position.copy(positionPoint);
    camera.current.lookAt(targetPoint);
    setCameraPosition();
    controls.current!.update();
    camera.current.updateProjectionMatrix();
  };

  const onButtonClick = (changeViewFunction: () => void) => {
    controls.current!.enabled = false;
    changeViewFunction();
    controls.current!.enabled = true;
  };

  return (
    <div id="render-cont">
      {withControlPanel && (
        <div className="flex gap-2 mb-2">
          <button
            className="border rounded-sm bg-color4 p-3 hover:bg-color3 hover:text-white"
            onClick={() => onButtonClick(changeViewRight)}
          >
            <BorderRight24Regular />
          </button>
          <button
            className="border rounded-sm bg-color4 p-3 hover:bg-color3 hover:text-white"
            onClick={() => onButtonClick(changeViewLeft)}
          >
            <BorderLeft24Regular />
          </button>
          <button
            className="border rounded-sm bg-color4 p-3 hover:bg-color3 hover:text-white"
            onClick={() => onButtonClick(changeViewFront)}
          >
            <BorderBottom24Regular />
          </button>
          <button
            className="border rounded-sm bg-color4 p-3 hover:bg-color3 hover:text-white"
            onClick={() => onButtonClick(changeViewBack)}
          >
            <BorderTop24Regular />
          </button>

          <button
            className="border rounded-sm bg-color4 p-3 hover:bg-color3 hover:text-white"
            onClick={() => onButtonClick(changeViewTopRightFrontCorner)}
          >
            <BorderTop24Regular />
          </button>

          <button
            className="border rounded-sm bg-color4 p-3 hover:bg-color3 hover:text-white"
            onClick={() => onButtonClick(changeViewTopLeftFrontCorner)}
          >
            <BorderTop24Regular />
          </button>

          <button
            className="border rounded-sm bg-color4 p-3 hover:bg-color3 hover:text-white"
            onClick={() => onButtonClick(changeViewTopLeftBackCorner)}
          >
            <BorderTop24Regular />
          </button>

          <button
            className="border rounded-sm bg-color4 p-3 hover:bg-color3 hover:text-white"
            onClick={() => onButtonClick(changeViewTopRightBackCorner)}
          >
            <BorderTop24Regular />
          </button>
        </div>
      )}

      <canvas
        ref={canvasRef as React.RefObject<HTMLCanvasElement>}
        id={canvasId}
        width="340px"
        height="340px"
        className={classNames('consist-render-box bg-slate-100! mb-2', {
          'cursor-move': editable,
        })}
      />
      {withControlPanel && (
        <div>
          <div className="text-sm flex flex-col pl-2">
            <span className="mb-2 uppercase text-xs">Vrstvy: </span>
            {layers.map((layer, i) => (
              <div key={i} className="flex gap-2 justify-between">
                <label className="cursor-pointer" htmlFor={String(i)}>
                  {layer.name}
                </label>
                <Checkbox
                  id={String(i)}
                  checked={layer.visible}
                  onCheckedChange={(checked) => {
                    onChangeLayerVisibility?.({ name: layer.name, visible: !!checked });
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default View;
